import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { CrudService } from './crud.service';
import { SessionStorageService } from './storage.service';
import { IAccount } from './user.service';

export interface IAvailableCountry {
	countryCode: string;
	name: string;
}

export interface IBaseCalendar {
	alias: string;
	description: string; // This property is not implemented
}

export interface ICalendar extends IBaseCalendar {
	id: string;
}

export interface ICalendars {
	result: ICalendar[];
}

export interface IRequestCalendar extends IBaseCalendar {
	// No additional properties
}

export interface IResponseCalendar extends IBaseCalendar {
	statusCode: number;
	result: ICalendar;
}

export interface ICalendarDay {
	date: Date;
	appointmentTypes: string;
	title: string;
}

export interface ICalendarDayAppointment {
	id: string;
	date: Date;
	title: string;
	appointmentType: string;
}

export interface IBaseAppointment {
	startTime: string | null;
	endTime: string | null;
	title: string | null;
	appointmentType: string | null;
	workTaskId: string | null;
	calendarId: string | null;
}

export interface IAppointment extends IBaseAppointment {
	id: string;
}

export interface IRequestAppointment extends IBaseAppointment {
	// No additional properties
}

export interface IAppointments {
	result: IAppointment[];
}

@Injectable({
	providedIn: 'root',
})
export class AppointmentService {
	constructor(
		private http: HttpClient,
		private crudService: CrudService,
		private translator: TranslateService,
		private sessionStorageService: SessionStorageService,
	) {}

	getHolidayCalendars(): Observable<IAvailableCountry[]> {
		return this.http.get<IAvailableCountry[]>('https://date.nager.at/api/v3/AvailableCountries');
	}

	getAppointmentTypes() {
		return [
			{
				value: 'None',
				text: this.translator.instant('mes.administration.appointments.appointment-types.none'),
			},
			{
				value: 'Weekend',
				text: this.translator.instant('mes.administration.appointments.appointment-types.weekend'),
			},
			{
				value: 'Vacation',
				text: this.translator.instant('mes.administration.appointments.appointment-types.vacation'),
			},
			{
				value: 'Holiday',
				text: this.translator.instant('mes.administration.appointments.appointment-types.holiday'),
			},
			{
				value: 'DayOff',
				text: this.translator.instant('mes.administration.appointments.appointment-types.day-off'),
			},
		];
	}

	createCalendar(calendar: ICalendar): Observable<void> {
		const body: IRequestCalendar = {
			alias: calendar.alias,
			description: '',
		};
		return this.crudService.create<IRequestCalendar, void>(body, '/mes/calendars');
	}

	getCalendars(): Observable<ICalendars> {
		const apiUrl = `${environment.awsEnvUri}/mes/calendars`;
		return this.http.get<ICalendars>(apiUrl, {
			headers: this.getHeaders(),
		});
	}

	deleteCalendarById(calendarId: string): Observable<any> {
		const apiUrl = `${environment.awsEnvUri}/mes/calendars/${calendarId}`;
		return this.http.delete(apiUrl, {
			headers: this.getHeaders(),
		});
	}

	createAppointment(appointment: IAppointment): Observable<void> {
		const body = this.mapToRequestAppointment(appointment);
		return this.crudService.create<IRequestAppointment, void>(body, `/mes/calendars/${appointment.calendarId}/appointments`);
	}

	updateAppointment(appointment: IAppointment): Observable<void> {
		const body = this.mapToRequestAppointment(appointment);
		return this.crudService.update<IRequestAppointment, void>(body, `/mes/calendars/${appointment.calendarId}/appointments/${appointment.id}`);
	}

	deleteAppointmentById(calendarId: string, appointmentId: string): Observable<any> {
		return this.crudService.delete(`/mes/calendars/${calendarId}/appointments/${appointmentId}`);
	}

	private mapToRequestAppointment(appointment: IAppointment): IRequestAppointment {
		// Convert startTime and endTime to Date objects
		const startDate = new Date(appointment.startTime);
		const endDate = new Date(appointment.endTime);

		// Adjust start and end times to UTC
		const startTime = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
		let endTime = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999));

		// Check if endTime is less than startTime
		if (endTime < startTime) {
			endTime = startTime;
		}

		return {
			startTime: startTime.toISOString(),
			endTime: endTime.toISOString(),
			title: appointment.title,
			appointmentType: appointment.appointmentType,
			calendarId: appointment.calendarId,
			workTaskId: appointment.workTaskId,
		};
	}

	getAppointmentsBetweenDates(calendarId: string, startDate: string, endDate: string): Observable<IAppointments> {
		const url = `${environment.awsEnvUri}/mes/calendars/${calendarId}/appointments?startDate=${startDate}&endDate=${endDate}`;
		return this.http.get<IAppointments>(url, { headers: this.getHeaders() });
	}

	getHeaders() {
		const h = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': (JSON.parse(this.sessionStorageService.get('User.Account')) as IAccount).id,
		};
		console.log(h);
		return h;
	}
}
